<script lang="ts" setup>
import {toRef} from "vue";
import {useField} from "vee-validate";
import {useTextareaAutosize} from "@vueuse/core";

const props = withDefaults(
  defineProps<{
    type?: string;
    id?: string;
    name: string;
    label: string;
    labelVisible?: boolean;
    value?: string;
    placeholder?: string;
    asRequired?: boolean;
    successMessage?: string;
    errorMessage?: string;
    rules?: string;
    element?: "input" | "textarea";
  }>(),
  {
    type: "text",
    element: "input",
    labelVisible: true,
  },
);

const name = toRef(props, "name");

const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  errors,
  meta,
} = useField(name, props.rules, {
  initialValue: props.value,
});

const {textarea} = useTextareaAutosize({
  input: inputValue,
  styleProp: "minHeight",
});

const {t} = useI18n();

const reinterpolate = (errorMessage: string, named: Record<string, any>) => {
  const asResolved = t(errorMessage, named, {resolvedMessage: true});
  if (asResolved !== errorMessage) {
    return asResolved;
  }
  return t(errorMessage, named, {resolvedMessage: false});
};

const _placeholder = computed(() => props.placeholder?.concat(props.asRequired ? ' *' : ''));

</script>

<template>
  <div
    :class="{'has-error': !!errorMessage, success: meta.valid}"
    class="text-input">
    <label :for="id ? id : name" v-if="labelVisible"
      >{{ label }} <sup v-if="asRequired">*</sup></label
    >
    <component
      :is="element"
      :id="id ? id : name"
      ref="textarea"
      :name="name"
      :placeholder="_placeholder"
      :rows="element === 'textarea' ? 4 : null"
      :type="type"
      :value="inputValue"
      @blur="handleBlur"
      @input="handleChange" />
    <div
      v-if="errorMessage"
      class="help-message mt-1 text-sunrise-100 page-light:text-sunrise-200"
      role="alert">
      {{ reinterpolate(errorMessage, {label, name, inputValue}) }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.text-input {
  display: flex;
  flex-direction: column;

  label {
    font-weight: bold;
    sup {
      color: var(--color-accent);
    }
  }

  label,
  [role="alert"] {
    margin-left: calc(var(--spacing) / 2);
  }

  .help-message {
    font-size: 0.75em;
  }

  input,
  textarea {
    background-color: rgba(255, 255, 255, 0);
    min-height: 40px;
    border-radius: 0;
    @apply border;
    font: inherit;
    padding: calc(var(--spacing) / 2) var(--spacing);
    color: var(--color-white);

    &:focus-visible {
      @apply page-dark:outline-white page-light:outline-black;
      outline-style: solid;
      outline-width: 2px;
    }

    &::placeholder {
      @apply page-dark:text-white page-light:text-black;
    }
  }

  textarea {
    resize: none;
  }

  textarea {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  textarea::-webkit-scrollbar {
    display: none;
  }

  ul {
    padding: 0;
    margin: 0;
    list-style-type: "- ";
    list-style-position: inside;
  }
}
</style>
